import React, { useCallback } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { ChartTypeCandles } from '@swyftx/aviary/icons/charts';
import { TickFilled } from '@swyftx/aviary/icons/filled';
import { Loading, MarginTrading, Swap } from '@swyftx/aviary/icons/outlined';
import { MenuItem } from '@swyftx/aviary/molecules/Menu';
import { MarginTraderAccount } from '@swyftx/margin/build/types';

import { cn } from '@shared/utils/lib/ui';

import { useAvo } from '@hooks/Avo/useAvo';

import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router';
import { useLayoutStore } from 'src/lib/layout';
import { useSwyftxMargin, useSwyftxMarginStore } from 'src/lib/margin';
import { useMarginAccounts } from 'src/lib/margin/hooks/account';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';
import { useSwyftxSpot } from 'src/lib/spot';
import { useSwyftxPro } from 'src/lib/trade-pro/hooks/useSwyftxPro';
import { useCurrentPlatform } from 'src/lib/utils/hooks/useCurrentPlatform';
import { SwyftxPlatform } from 'src/lib/utils/utils.types';

type Props = {
  className?: string;
};

const SwitchPlatformMenuItem: React.FC<Props> = observer(({ className }) => {
  const { setCurrentPlatform, currentPlatform } = useCurrentPlatform();
  const { swyftxProEnabled, isSwyftxPro } = useSwyftxPro();
  const { isSwyftxSpot } = useSwyftxSpot();
  const avo = useAvo();
  const location = useLocation();
  const { data, isFetching } = useMarginAccounts();
  const { setTraderAccountId } = useSwyftxMarginStore();
  const { setMobileMenuOpen, setSwitchPlatform } = useLayoutStore();
  const { navigate } = useNavigateRoute();
  const { swyftxMarginEnabled, isSwyftxMargin } = useSwyftxMargin();

  const updateCurrentPlatform = useCallback(
    (platform: SwyftxPlatform, account?: MarginTraderAccount) => {
      if (currentPlatform === platform) return;

      setCurrentPlatform(platform);
      setSwitchPlatform(platform);
      setMobileMenuOpen(false);

      if (account) {
        setTraderAccountId(account.traderAccountId);
        navigate(NavigationURLs.MarginAccount);
      }

      avo.switchPlatformButtonClicked({ screen: location.pathname, buttonName: platform });
    },
    [
      avo,
      currentPlatform,
      location.pathname,
      navigate,
      setCurrentPlatform,
      setMobileMenuOpen,
      setSwitchPlatform,
      setTraderAccountId,
    ],
  );

  if (!swyftxProEnabled && !swyftxMarginEnabled) return null;

  return (
    <>
      <Separator className='mt-8' />

      <FlexLayout direction='column' spacing={4} className={cn('flex-wrap', className)}>
        <Body size='small' color='secondary' weight='bold' className='my-4 px-8'>
          Switch Platform
        </Body>
        <MenuItem
          key='swyftx'
          icon={
            <FlexLayout
              alignItems='center'
              justifyContent='center'
              className='min-h-24 min-w-24 rounded-8 bg-color-background-secondary p-4'
            >
              <Swap className='h-24 w-24 text-color-text-inverse' />
            </FlexLayout>
          }
          endAdornment={isSwyftxSpot ? <TickFilled className='h-16 w-16 text-color-text-accent' /> : undefined}
          text='Spot'
          description='Buy, sell & swap'
          action={() => updateCurrentPlatform('swyftx')}
        />
        {isFetching && (
          <FlexLayout alignItems='center' justifyContent='center' className='w-full p-4'>
            <Loading className='w-16 animate-spin text-color-text-primary' />
          </FlexLayout>
        )}
        {!!data?.accounts.length && !isFetching && swyftxMarginEnabled && data.accounts?.[0].active && (
          <>
            <MenuItem
              key='swyftx-derivatives'
              icon={
                <FlexLayout className='min-h-24 min-w-24 rounded-8 bg-color-background-secondary p-4'>
                  <MarginTrading className='h-24 w-24 text-color-text-inverse' />
                </FlexLayout>
              }
              endAdornment={isSwyftxMargin ? <TickFilled className='h-16 w-16 text-color-text-accent' /> : undefined}
              text='Derivatives'
              description='Trade with leverage'
              action={() => updateCurrentPlatform('derivatives', data.accounts[0])}
            />
          </>
        )}
        {swyftxProEnabled && (
          <MenuItem
            key='swyftx-pro'
            icon={
              <div className='flex h-32 w-32 items-center rounded-8 bg-color-background-secondary p-4'>
                <ChartTypeCandles className='h-24 w-24 text-color-text-inverse' />
              </div>
            }
            endAdornment={isSwyftxPro ? <TickFilled className='h-16 w-16 text-color-text-accent' /> : undefined}
            text='Swyftx Pro'
            description='Advanced trading tools'
            action={() => updateCurrentPlatform('swyftx-pro')}
          />
        )}
      </FlexLayout>
    </>
  );
});

export { SwitchPlatformMenuItem };
